import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardBillingBLInterfaceUX from "./CardBillingBLInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox, Icon } from "semantic-ui-react";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/BillingBLInterface";

// Serializer
import BillingBLInterfaceSerializer from "issara-sdk/types/BillingBLInterfaceSerializer_apps_INF";

// Type
type CardBillingBLInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  BillingBLInterfaceSequence: State["BillingBLInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardBillingBLInterface = (props: CardBillingBLInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedBillingBL, setSelectedBillingBL] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "BillingBLInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const columnTableIconCheck = (data: boolean, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : (
          <Icon name="times circle" color="red" size="big" />
        )}
      </div>
    );
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.BillingBLInterfaceSequence?.billingBLInterfaceList || []).map(
        (item: BillingBLInterfaceSerializer) => item.id
      );

      setSelectedBillingBL(newData);
    } else {
      setSelectedBillingBL([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "BillingBLInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedBillingBL([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "BillingBLInterface",
      action: "SAVE",
      selectedItems: selectedBillingBL,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedBillingBL([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "BillingBLInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedBillingBL([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`BillingBLInterfaceSequence.searchBillingBL.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`BillingBLInterfaceSequence.searchBillingBL.createdDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`BillingBLInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "BillingBLInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedBillingBL([]);
    props.setProp(`BillingBLInterfaceSequence.selectedItems`, []);
  };

  // const handleDisabledTrigger = useMemo(() => {
  //   if (selectedBillingBL.length === 0) return true;

  //   return !selectedBillingBL.every((id) => {
  //     const row = props.BillingBLInterfaceSequence?.billingBLInterfaceList?.find(
  //       (item: BillingBLInterfaceSerializer) => item.id === id
  //     );

  //     return row?.valid === false;
  //   });
  // }, [selectedBillingBL, props.BillingBLInterfaceSequence?.billingBLInterfaceList]);

  // const handleDisabledSent = useMemo(() => {
  //   if (selectedBillingBL.length === 0) return true;

  //   return !selectedBillingBL.every((id) => {
  //     const row = props.BillingBLInterfaceSequence?.billingBLInterfaceList?.find(
  //       (item: BillingBLInterfaceSerializer) => item.id
  //     );

  //     return row?.valid === true && row?.sent === false;
  //   });
  // }, [selectedBillingBL, props.BillingBLInterfaceSequence?.billingBLInterfaceList]);

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedBillingBL.length === 0) return true;
  }, [selectedBillingBL]);

  // useMemo
  const billingBLInterfaceList = useMemo(() => {
    return (props.BillingBLInterfaceSequence?.billingBLInterfaceList || []).map(
      (item: BillingBLInterfaceSerializer) => {
        let createdName = (props.BillingBLInterfaceSequence?.userOptions || []).find(
          (data: any) => data?.id === item.created_by
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedBillingBL.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedBillingBL([...selectedBillingBL, item.id]);
                } else {
                  let newData = selectedBillingBL.filter((key: any) => key !== item.id);
                  setSelectedBillingBL(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof BillingBLInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;
              
              if (key === "created_by") {
                data[key] = columnTableCenter(
                  (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
                );
              } else if (["valid", "sent"].includes(key)) {
                data[key] = columnTableIconCheck(!!item[key]);
              } else if (dateTimeColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            }, {}
          ),
        };
      }
    );
  }, [
    selectedBillingBL,
    props.BillingBLInterfaceSequence?.billingBLInterfaceList,
    props.BillingBLInterfaceSequence?.userOptions,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardBillingBLInterfaceUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchBillingBL={props.BillingBLInterfaceSequence?.searchBillingBL}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedBillingBL.length ===
                    (props.BillingBLInterfaceSequence?.billingBLInterfaceList || [])
                      .length &&
                  (props.BillingBLInterfaceSequence?.billingBLInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        billingBLInterfaceList={billingBLInterfaceList}
        paginatedItems={props.BillingBLInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        addButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("ADD")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_ADD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_ADD`]}
            // config
            color="blue"
            title="Add Record"
            name="ADD"
            size="medium"
          />
        }
        editButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("EDIT")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_EDIT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_EDIT`]}
            // config
            color="green"
            title="Edit Date"
            name="EDIT"
            size="medium"
          />
        }
      />
    </>
  );
};

CardBillingBLInterface.displayName = "CardBillingBLInterface";

export default React.memo(CardBillingBLInterface);

const dateTimeColumns: Partial<keyof BillingBLInterfaceSerializer>[] = [
  "created_at"
];

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  sent: 70,
  created_by: 120,
  created_at: 150,
  file_name: 160,
  version: 80,
  record_type: 80,
  status: 70,
};

const tableHeaders = GenerateTableHeader(columnWidths);

// const tableHeaders: {
//   Header: string;
//   accessor: keyof BillingBLInterfaceSerializer;
//   width?: number;
// }[] = [
//   {
//     Header: "ID",
//     accessor: "id",
//     width: 70,
//   },
//   {
//     Header: "Valid",
//     accessor: "valid",
//     width: 70,
//   },
//   {
//     Header: "Error Message",
//     accessor: "error_message",
//   },
//   {
//     Header: "User ที่สร้าง",
//     accessor: "created_by",
//     width: 120,
//   },
//   {
//     Header: "วันเวลาที่สร้าง",
//     accessor: "created_at",
//   },
//   {
//     Header: "Sent",
//     accessor: "sent",
//     width: 70,
//   },
//   {
//     Header: "File Name",
//     accessor: "file_name",
//     width: 160,
//   },
//   {
//     Header: "Version",
//     accessor: "version",
//     width: 80,
//   },
//   {
//     Header: "Invoice Item",
//     accessor: "invoice_item",
//   },
//   {
//     Header: "Record Type",
//     accessor: "record_type",
//     width: 80,
//   },
//   {
//     Header: "Company Code",
//     accessor: "company_code",
//   },
//   {
//     Header: "Document No",
//     accessor: "document_no",
//   },
//   {
//     Header: "Status",
//     accessor: "status",
//     width: 70,
//   },
//   {
//     Header: "Document Type",
//     accessor: "document_type",
//   }
// ];
